import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Upload, UserPlus } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { useLanguage } from "../contexts/LanguageContext";

export default function AddIntervieweeDialog({ open, onOpenChange, onAdd }) {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [type, setType] = useState("Leadership");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [aiPolocyGuidelines, setAiPolocyGuidelines] = useState("No");
  const [csvData, setCsvData] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const fileInputRef = useRef(null);

  const handleAddSingle = () => {
    if (!name.trim() || !position.trim()) {
      toast({
        title: t("Missing fields"),
        description: t("Name and Position are required."),
        variant: "destructive",
      });
      return;
    }
    onAdd([
      {
        name: name.trim(),
        position: position.trim(),
        aiPolocyGuidelines,
      },
    ]);
    setName("");
    setRole("");
    setType("Leadership");
    setEmail("");
    setPhone("");
    setDepartment("");
    setPosition("");
    setAiPolocyGuidelines("No");
    onOpenChange(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast({
        title: t("Invalid file"),
        description: t("Please upload a CSV file."),
        variant: "destructive",
      });
      return;
    }

    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      const lines = text.split("\n").filter((line) => line.trim());
      const startIdx = lines[0]?.toLowerCase().includes("name") ? 1 : 0;

      const parsed = lines
        .slice(startIdx)
        .map((line) => {
          const parts = line
            .split(",")
            .map((p) => p.trim().replace(/^["']|["']$/g, ""));
          return {
            name: parts[0] || "",
            role: parts[1] || "",
            type: parts[2] || "Leadership",
            email: parts[3] || undefined,
            phone: parts[4] || undefined,
          };
        })
        .filter((p) => p.name);

      setCsvData(parsed);
    };
    reader.readAsText(file);
  };

  const handleUploadCSV = () => {
    if (csvData.length === 0) return;
    onAdd(csvData);
    setCsvData([]);
    setCsvFileName("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            {t("Add Interviewees")}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="single" className="w-full">
          {/* <TabsList className="grid w-full grid-cols-1">
            <TabsTrigger value="single" className="gap-1.5">
              <UserPlus className="h-3.5 w-3.5" />
              Single
            </TabsTrigger>
            <TabsTrigger value="csv" className="gap-1.5">
              <Upload className="h-3.5 w-3.5" />
              CSV Upload
            </TabsTrigger>
          </TabsList> */}

          <TabsContent value="single" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="interviewee-name">{t("Name")}</Label>
                <Input
                  id="interviewee-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interviewee-position">
                  {t("form.position")}
                </Label>
                <Input
                  id="interviewee-position"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="Manager"
                  maxLength={100}
                />
              </div>
              {/* <div className="space-y-2">
                <Label htmlFor="interviewee-dept">Department</Label>
                <Input
                  id="interviewee-dept"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="Sales"
                  maxLength={100}
                />
              </div> */}

              {/* <div className="space-y-2">
                <Label htmlFor="interviewee-type">Type</Label>
                <select
                  id="interviewee-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="Leadership">Leadership</option>
                  <option value="Management">Management</option>
                  <option value="Staff">Staff</option>
                  <option value="Technical">Technical</option>
                </select>
              </div> */}
              {/* <div className="space-y-2">
                <Label htmlFor="interviewee-email">Email</Label>
                <Input
                  id="interviewee-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@company.com"
                  maxLength={255}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interviewee-phone">Phone</Label>
                <Input
                  id="interviewee-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  maxLength={20}
                />
              </div> */}
              <div className="space-y-2 sm:col-span-2">
                <Label>{t("AI Policies & Guidelines")}</Label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="aiPolocyGuidelines"
                      value="Yes"
                      checked={aiPolocyGuidelines === "Yes"}
                      onChange={() => setAiPolocyGuidelines("Yes")}
                    />
                    {t("Yes")}
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="aiPolocyGuidelines"
                      value="No"
                      checked={aiPolocyGuidelines === "No"}
                      onChange={() => setAiPolocyGuidelines("No")}
                    />
                    {t("No")}
                  </label>
                </div>
              </div>
            </div>
            <Button
              onClick={handleAddSingle}
              disabled={!name.trim() || !position.trim()}
              className="w-full"
            >
              {t("Add Interviewee")}
            </Button>
          </TabsContent>

          <TabsContent value="csv" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{t("Upload CSV")}</Label>
              <p className="text-xs text-muted-foreground">
                {t(
                  "CSV format: Name, Role, Type, Email, Phone (one per row). First row can be a header.",
                )}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4" />
                {csvFileName || t("Choose CSV file")}
              </Button>
            </div>

            {csvData.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {csvData.length} {t("interviewee(s) found")}:
                </p>
                <div className="max-h-40 overflow-auto rounded-md border p-2 space-y-1">
                  {csvData.map((person, i) => (
                    <div key={i} className="text-xs text-muted-foreground">
                      {person.name} — {person.role} ({person.type})
                      {person.email ? ` · ${person.email}` : ""}
                      {person.phone ? ` · ${person.phone}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              onClick={handleUploadCSV}
              disabled={csvData.length === 0}
              className="w-full"
            >
              {t("Add")} {csvData.length} {t("Interviewees")}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
